"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pageRootBlockId = pageRootBlockId;
exports.pageKindMissingPart = pageKindMissingPart;
exports.pageBlockIdsOfKind = pageBlockIdsOfKind;
exports.pagePlacedBlockIds = pagePlacedBlockIds;
exports.pageUnplacedBlockIds = pageUnplacedBlockIds;
exports.pagePlacedElementIds = pagePlacedElementIds;
exports.pagePartsWithKind = pagePartsWithKind;
const page_document_maps_1 = require("./page-document-maps");
const page_part_candidates_1 = require("./page-part-candidates");
const page_part_directory_1 = require("./page-part-directory");
const page_read_source_1 = require("./page-read-source");
const page_structure_ancestors_1 = require("./page-structure-ancestors");
const page_structure_tree_1 = require("./page-structure-tree");
const page_structure_writer_1 = require("./page-structure-writer");
/** The placed root of a page, or null when no root is placed. */
function pageRootBlockId(doc) {
    return (0, page_structure_writer_1.pageDocumentTree)(doc).root;
}
/**
 * Reads the entries a kind lookup needs and the first part it still misses:
 * a part whose summary holds the kind, or one that may hold a missing
 * ancestor of a match.
 */
function readKindLookup(source, kind) {
    const loaded = new Set(source.loadedPartIds());
    const directory = (0, page_part_directory_1.readPagePartDirectory)(source.head);
    const missingPart = (id) => (0, page_part_candidates_1.pagePartsMayHoldId)(directory, id, partId => !loaded.has(partId))[0];
    const { entries } = (0, page_read_source_1.readPage)(source);
    const missing = () => {
        for (const partId of pagePartsWithKind(source.head, kind))
            if (!loaded.has(partId))
                return partId;
        for (const [id, entry] of entries) {
            if (entry.k !== kind)
                continue;
            for (const [ancestorId, ancestor] of (0, page_structure_ancestors_1.pageStructureAncestors)(entries, id)) {
                if (!ancestor) {
                    const partId = missingPart(ancestorId);
                    if (partId !== undefined)
                        return partId;
                }
            }
        }
        return undefined;
    };
    return { entries, missingPart, missing: missing() };
}
/** The first part a lookup of the kind still needs, or undefined when none. */
function pageKindMissingPart(source, kind) {
    return readKindLookup(source, kind).missing;
}
/** Placed blocks of a kind in depth-first reading order. */
function pageBlockIdsOfKind(source, kind) {
    const { entries, missingPart, missing } = readKindLookup(source, kind);
    if (missing !== undefined)
        throw new page_read_source_1.PageReadPartMissingError(missing);
    const tree = (0, page_structure_tree_1.derivePageTree)(entries, undefined, id => missingPart(id) !== undefined);
    return pagePlacedBlockIds(tree).filter(id => entries.get(id)?.k === kind);
}
/** Placed blocks in depth-first reading order, including recovered children. */
function pagePlacedBlockIds(tree) {
    if (tree.root === null)
        return [];
    const result = [];
    const pending = [tree.root];
    while (pending.length > 0) {
        const id = pending.pop();
        if (id !== page_structure_tree_1.PAGE_TREE_RECOVERED_ID)
            result.push(id);
        pending.push(...(0, page_structure_tree_1.pageTreeChildIds)(tree, id).reverse());
    }
    return result;
}
/** Blocks of the page without a place in its tree, by id. */
function pageUnplacedBlockIds(entries) {
    return [...entries]
        .filter(([, entry]) => entry.p === page_structure_tree_1.PAGE_TREE_UNPLACED_ID)
        .map(([id]) => id)
        .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}
/** Placed elements in surface reading order and then layer order. */
function pagePlacedElementIds(tree) {
    return pagePlacedBlockIds(tree).flatMap(id => [
        ...(tree.elements.get(id) ?? []),
    ]);
}
/** Parts that may hold a kind; missing summaries cannot prove absence. */
function pagePartsWithKind(head, kind) {
    return [...(0, page_part_directory_1.readPagePartDirectory)(head)]
        .filter(([id, { m }]) => id !== page_document_maps_1.PAGE_DOCUMENT_PART_ID && (!m || m.kinds.includes(kind)))
        .map(([id]) => id);
}
